import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/services/auth.service';
import { NameValuePair } from '../../../models/name-value-pair.model';
import { DataFilters, DataFiltersService } from '../../../services/data-filters.service';

export type MapHeaderTitle = 'annual_per_capita' |  'people';
export type MapHeaderType = 'share_of_world' | 'share_of_country';


export type MapHeaderData = {
  title: MapHeaderTitle;
  type: MapHeaderType;
}

@Component({
  selector: 'mpro-map-header-categories',
  templateUrl: './map-header-categories.component.html',
  styleUrls: ['./map-header-categories.component.scss']
})
export class MapHeaderCategoriesComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public selectedYear: number;
  @Input() public showCountriesOutsideRegion: boolean = false;
  @Output() public mapHeaderDataChanged: EventEmitter<MapHeaderData> = new EventEmitter();

  public mapHeaderForm: UntypedFormGroup;
  public mapHeaderTitles: NameValuePair[] = [
    new NameValuePair('Annual spending per capita', 'annual_per_capita'),
    new NameValuePair('People', 'people')
  ];


  public mapHeaderTypes: NameValuePair[] = [];
  public typeDropdownSelectedValue: NameValuePair;
  public titleDropdownSelectedValue: NameValuePair;
  public typeDropdownDisabled: boolean = false;
  private defaultRegionName: string = 'The World';
  private currentRegionName: string = '';
  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    private dataFiltersService: DataFiltersService,
    private fb: UntypedFormBuilder,
    private authService: AuthService) {
    this.mapHeaderTypes = [
      new NameValuePair(this.authService.isLoggedIn() ? 'Share of country' : 'Share of continent', 'share_of_country')
    ];
    this.typeDropdownSelectedValue = DataFilters.getNameValuePair(this.mapHeaderTypes, 'share_of_world');
    this.titleDropdownSelectedValue = DataFilters.getNameValuePair(this.mapHeaderTitles, 'annual_per_capita');
  }

  public ngOnInit(): void {
    this.initializeForm();
    this.dataFiltersService.getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(filters => {
        this.currentRegionName = filters.regionSelection.getName();
        this.updateRegionName();
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.showCountriesOutsideRegion) {
      this.updateRegionName();
    }
  }

  public onTypeChange(type: NameValuePair, emitEvent: boolean = true) {
    this.typeDropdownSelectedValue = type;
    this.mapHeaderForm.patchValue({ type: type.value }, { emitEvent });
  }

  public onTitleChange(title: NameValuePair, emitEvent: boolean = true) {
    this.titleDropdownSelectedValue = title;
    this.mapHeaderForm.patchValue({ title: title.value }, { emitEvent });
  }

  private updateRegionName() {
    let regionName = this.currentRegionName;
    if (this.showCountriesOutsideRegion) {
      regionName = this.defaultRegionName;
    }

    this.mapHeaderTypes = this.mapHeaderTypes.map(type => {
      return type;
    });

    this.typeDropdownSelectedValue = DataFilters.getNameValuePair(this.mapHeaderTypes, this.typeDropdownSelectedValue.value);
  }

  private initializeForm(): void {
    this.mapHeaderForm = this.fb.group({
      title: ['annual_per_capita', Validators.required],
      type: ['share_of_country', Validators.required],
    });
    this.mapHeaderDataChanged.emit(this.mapHeaderForm.value);
    this.mapHeaderForm.controls.title.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(title => {
        if (title === 'annual_per_capita') {
          this.onTypeChange(this.mapHeaderTypes[0], false);
          this.typeDropdownDisabled = true;
        } else {
          this.typeDropdownDisabled = false;
        }
      });

    this.mapHeaderForm.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => this.mapHeaderDataChanged.emit(value));
  }
}
